import React, { useState, useEffect } from "react";
import { Layout, theme, Button, Drawer, Grid, Dropdown, message } from "antd";
import {
  LogoutOutlined,
  MenuOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  GlobalOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import Title from "antd/es/typography/Title";
import "./App.css";

import AppMenu from "./components/layout/Menu";
import Login from "./components/layout/Login";
import { getRoutes } from "./config/appConfig";
import { isAuthenticated, clearAuth } from "./services/axiosInstance";
import UserService from "./services/UserService";

const { Header, Content, Sider } = Layout;
const { useBreakpoint } = Grid;

const FLAG_URL =
  "https://upload.wikimedia.org/wikipedia/en/4/41/Flag_of_India.svg";

const App = () => {
  const [selectedKey, setSelectedKey] = useState("home");
  const [collapsed, setCollapsed] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [loggedIn, setLoggedIn] = useState(isAuthenticated());
  const [username, setUsername] = useState("");

  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const { i18n } = useTranslation();

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const currentLang = i18n.language || "en";

  const languageItems = [
    { key: "en", label: "English" },
    { key: "mr", label: "मराठी" },
  ];

  const handleLanguageChange = ({ key }) => {
    i18n.changeLanguage(key);
  };

  // Fetch username on login
  useEffect(() => {
    if (!loggedIn) return;

    UserService.getUsername()
      .then((res) =>
        setUsername(res.data.user.username || res.data.user.name || "")
      )
      .catch((err) => console.error("Failed to fetch username:", err));
  }, [loggedIn]);

  // Auto-collapse on smaller screens
  useEffect(() => {
    if (screens.lg === false && screens.md === true) {
      setCollapsed(true);
    } else if (screens.lg === true) {
      setCollapsed(false);
    }
  }, [screens]);

  const handleMenuClick = (key) => {
    setSelectedKey(key);
    if (isMobile) {
      setMobileDrawerOpen(false);
    }
  };

  const handleLogout = () => {
    try {
      clearAuth();
      message.success({
        content: "Logged out successfully!",
        duration: 2,
      });
      setLoggedIn(false);
    } catch (error) {
      console.error("Logout error:", error);
      message.error({
        content: "Logout failed. Please try again.",
        duration: 3,
      });
    }
  };

  const routes = getRoutes();

  const renderContent = () => {
    const Component = routes[selectedKey];
    return Component ? <Component /> : "Select a menu item";
  };

  if (!loggedIn) {
    return <Login onLoginSuccess={() => setLoggedIn(true)} />;
  }

  // Sider content (reused in both desktop and mobile)
  const siderContent = (
    <AppMenu onMenuClick={handleMenuClick} collapsed={collapsed && !isMobile} />
  );

  return (
    <Layout style={{ minHeight: "100vh" }}>
      {/* Header */}
      <Header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: isMobile ? "0 12px" : "0 24px",
          background: "#001529",
          position: "sticky",
          top: 0,
          zIndex: 100,
          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          {/* Mobile menu toggle */}
          {isMobile && (
            <Button
              type="text"
              icon={<MenuOutlined />}
              onClick={() => setMobileDrawerOpen(true)}
              style={{ color: "white", fontSize: "18px" }}
            />
          )}

          {/* Desktop collapse toggle */}
          {!isMobile && (
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              style={{ color: "white", fontSize: "18px" }}
            />
          )}

          <img
            src={FLAG_URL}
            alt="Indian Flag"
            style={{ height: isMobile ? "24px" : "32px" }}
          />

          {!isMobile && (
            <Title level={4} style={{ color: "white", margin: 0 }}>
              GP Admin Panel
            </Title>
          )}
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: isMobile ? "8px" : "16px",
          }}
        >
          {username && (
            <span
              style={{
                color: "rgba(255,255,255,0.85)",
                fontSize: isMobile ? "12px" : "14px",
              }}
            >
              Welcome, <strong>{username}</strong>
            </span>
          )}

          {/* Language Selector */}
          <Dropdown
            menu={{
              items: languageItems,
              onClick: handleLanguageChange,
              selectedKeys: [currentLang],
            }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <Button
              type="text"
              icon={<GlobalOutlined />}
              style={{
                color: "white",
                border: "1px solid rgba(255,255,255,0.3)",
                borderRadius: "6px",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              {!isMobile && (currentLang === "en" ? "English" : "मराठी")}
            </Button>
          </Dropdown>

          <Button
            type="text"
            icon={<LogoutOutlined />}
            onClick={handleLogout}
            style={{
              color: "white",
              border: "1px solid rgba(255,255,255,0.3)",
              borderRadius: "6px",
            }}
          >
            {!isMobile && "Logout"}
          </Button>
        </div>
      </Header>

      <Layout>
        {/* Desktop Sider */}
        {!isMobile && (
          <Sider
            width={260}
            collapsedWidth={80}
            collapsed={collapsed}
            style={{
              background: "linear-gradient(180deg, #001529 0%, #042035 100%)",
              overflow: "auto",
              height: "calc(100vh - 64px)",
              position: "sticky",
              top: 64,
              left: 0,
              boxShadow: "2px 0 8px rgba(0,0,0,0.15)",
            }}
          >
            {siderContent}
          </Sider>
        )}

        {/* Mobile Drawer */}
        <Drawer
          title={
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <img
                src={FLAG_URL}
                alt="Indian Flag"
                style={{ height: "24px" }}
              />
              <span>GP Admin Panel</span>
            </div>
          }
          placement="left"
          onClose={() => setMobileDrawerOpen(false)}
          open={mobileDrawerOpen}
          width={280}
          styles={{
            body: { padding: 0, background: "#062233" },
            header: {
              background: "#001529",
              color: "white",
              borderBottom: "1px solid rgba(255,255,255,0.1)",
            },
          }}
          closeIcon={<MenuFoldOutlined style={{ color: "white" }} />}
        >
          {siderContent}
        </Drawer>

        {/* Main Content */}
        <Layout style={{ padding: isMobile ? "12px" : "24px" }}>
          <Content
            style={{
              padding: isMobile ? 16 : 24,
              background: colorBgContainer,
              borderRadius: borderRadiusLG,
              minHeight: "calc(100vh - 64px - 48px)",
              overflow: "auto",
            }}
          >
            {renderContent()}
          </Content>
        </Layout>
      </Layout>
    </Layout>
  );
};

export default App;
